import { IBatchStatsOffline } from '@/contexts/sync-managements/offline-data-service';
import { ListSimpleAnimalResponse, ListSimpleAnimalResponseGtasItem } from '@/query';
import { create } from 'zustand';

export enum IdentificationSteps {
  Sisbov = 'Sisbov',
  Chip = 'Chip',
  Peso = 'Peso',
  BrincoExtra = 'Brinco extra',
  Dentição = 'Dentição',
  Sorting = 'Apartação'
}

export const ordinationNameToStep: Record<string, IdentificationSteps> = {
  sisbov: IdentificationSteps.Sisbov,
  electronicEarring: IdentificationSteps.Chip,
  weight: IdentificationSteps.Peso,
  otherEarring: IdentificationSteps.BrincoExtra,
  dentitionStage: IdentificationSteps.Dentição
};

export interface SelectOption {
  label: string;
  value: string;
}

export interface DefaultConfig<T = any> {
  type: string;
  defaultValue: T;
}

export interface OrdinationItem {
  id: string;
  name: string;
  content: string;
}

const DEFAULT_IDENTIFICATION_STEPS: IdentificationSteps[] = [
  IdentificationSteps.Chip,
  IdentificationSteps.Sisbov,
  IdentificationSteps.Peso,
  IdentificationSteps.BrincoExtra,
  IdentificationSteps.Dentição,
  IdentificationSteps.Sorting
];

function buildIdentificationSteps(ordination?: OrdinationItem[]) {
  if (!ordination?.length) {
    return DEFAULT_IDENTIFICATION_STEPS;
  }

  const availableSteps = new Set<IdentificationSteps>();
  const steps: IdentificationSteps[] = [];

  for (const item of ordination) {
    const mappedStep = ordinationNameToStep[item.name];

    if (mappedStep) {
      availableSteps.add(mappedStep);
    }
  }

  for (const step of DEFAULT_IDENTIFICATION_STEPS) {
    if (step !== IdentificationSteps.Sorting && availableSteps.has(step)) {
      steps.push(step);
    }
  }

  if (!steps.includes(IdentificationSteps.Sorting)) {
    steps.push(IdentificationSteps.Sorting);
  }

  return steps.length > 0 ? steps : DEFAULT_IDENTIFICATION_STEPS;
}

export interface DestinationConfig {
  port: string;
  color: string;
  corral: SelectOption;
  maxWeight: number;
  minWeight: number;
}

export interface IdentificationData {
  uuid: string;
  name: string;
  createAt: string;
  updateAt: string;
  age: DefaultConfig<string>;
  batches: SelectOption[];
  breedBND: DefaultConfig<SelectOption[]>;
  ageFilter: string | null;
  breedFarm: DefaultConfig<SelectOption[]>;
  ordination: OrdinationItem[];
  destination: DestinationConfig[];
  medications: DefaultConfig<null>;
  otherEarring: string;
  dentitionStage: string;
  managementType: string;
  healthProtocols: DefaultConfig<SelectOption>;
}

export interface OxIdentification {
  sisbov: string | null;
  chip: string | null;
  weight: number | null;
  chipExtra: string | null;
  dentitionStage: string | null;
}

export interface AppStore {
  data: IdentificationData | null;
  batch: ListSimpleAnimalResponse | null;
  gta: ListSimpleAnimalResponseGtasItem | null;
  batchStats: IBatchStatsOffline[] | null;
  step: IdentificationSteps;
  steps: IdentificationSteps[];
  currentStepIndex: number;
  ox: OxIdentification;
  lastOxManaged: Date | null;
  useNetwork: boolean;
  isMaster: boolean;
  requireSisbov: boolean;
  customHealthProtocol: SelectOption | null;
  customAge: string | null;
  noAnimalAvailable: boolean;
  submitSorting: (() => Promise<boolean>) | null;
  returnToSortingAfterFix: boolean;

  setData: (props: IdentificationData | null) => void;
  setBatchAndGta: (props: {
    batch: ListSimpleAnimalResponse | null;
    gta: ListSimpleAnimalResponseGtasItem | null;
  }) => void;
  setBatchStats: (props: IBatchStatsOffline[] | null) => void;
  setStep: (props: IdentificationSteps) => void;
  setOx: (props: OxIdentification) => void;
  setUseNetwork: (props: boolean) => void;
  setIsMaster: (props: boolean) => void;
  setRequireSisbov: (props: boolean) => void;
  setLastOxManaged: () => void;
  setCustomHealthProtocol: (props: SelectOption) => void;
  setCustomAge: (props: string) => void;
  setNoAnimalAvailable: (value: boolean) => void;
  setSubmitSorting: (fn: (() => Promise<boolean>) | null) => void;
  resetForNewIdentification: () => void;

  nextStep: () => void;
  onSuccess: (props?: { skip?: boolean }) => void;

  reset: () => void;
}

export const useIdentificationManagementStore = create<AppStore>()((set) => ({
  data: null,
  batch: null,
  gta: null,
  batchStats: null,
  step: IdentificationSteps.Chip,
  steps: DEFAULT_IDENTIFICATION_STEPS,
  currentStepIndex: 0,
  ox: {
    sisbov: null,
    chip: null,
    weight: null,
    chipExtra: null,
    dentitionStage: null
  },
  lastOxManaged: null,
  useNetwork: false,
  isMaster: false,
  requireSisbov: false,
  customHealthProtocol: null,
  customAge: null,
  noAnimalAvailable: false,
  submitSorting: null,
  returnToSortingAfterFix: false,

  setData: (props: IdentificationData | null) =>
    set(() => {
      if (!props) {
        return {
          data: null,
          step: DEFAULT_IDENTIFICATION_STEPS[0],
          steps: DEFAULT_IDENTIFICATION_STEPS,
          currentStepIndex: 0
        };
      }

      const steps = buildIdentificationSteps(props.ordination);

      return {
        data: props,
        steps,
        step: steps[0],
        currentStepIndex: 0
      };
    }),
  setBatchAndGta: (props: { batch: ListSimpleAnimalResponse | null; gta: ListSimpleAnimalResponseGtasItem | null }) =>
    set(() => ({ batch: props.batch, gta: props.gta })),
  setBatchStats: (props: IBatchStatsOffline[] | null) => set(() => ({ batchStats: props })),
  setStep: (props: IdentificationSteps) =>
    set((state) => {
      const stepIndex = state.steps.indexOf(props);

      if (stepIndex === -1) {
        return {
          step: state.step,
          currentStepIndex: state.currentStepIndex
        };
      }

      const navigatingBackFromSorting =
        state.step === IdentificationSteps.Sorting && props !== IdentificationSteps.Sorting;

      return {
        step: props,
        currentStepIndex: stepIndex,
        returnToSortingAfterFix: navigatingBackFromSorting ? true : state.returnToSortingAfterFix
      };
    }),
  setOx: (props: OxIdentification) => set(() => ({ ox: props })),
  setUseNetwork: (props: boolean) => set(() => ({ useNetwork: props })),
  setIsMaster: (props: boolean) => set(() => ({ isMaster: props })),
  setRequireSisbov: (props: boolean) => set(() => ({ requireSisbov: props })),
  setLastOxManaged: () => set(() => ({ lastOxManaged: new Date() })),
  setCustomHealthProtocol: (props: SelectOption) => set(() => ({ customHealthProtocol: props })),
  setCustomAge: (props: string) => set(() => ({ customAge: props })),
  setNoAnimalAvailable: (value: boolean) => set(() => ({ noAnimalAvailable: value })),
  setSubmitSorting: (fn) => set(() => ({ submitSorting: fn })),
  resetForNewIdentification: () =>
    set((state) => ({
      step: state.steps[0] || IdentificationSteps.Chip,
      currentStepIndex: 0,
      returnToSortingAfterFix: false,
      ox: {
        sisbov: null,
        chip: null,
        weight: null,
        chipExtra: null,
        dentitionStage: null
      }
    })),

  nextStep: () =>
    set((state) => {
      if (state.returnToSortingAfterFix) {
        const sortingIndex = state.steps.indexOf(IdentificationSteps.Sorting);

        if (sortingIndex !== -1) {
          return {
            step: IdentificationSteps.Sorting,
            currentStepIndex: sortingIndex,
            returnToSortingAfterFix: false
          };
        }
      }

      const nextStepIndex = Math.min(state.currentStepIndex + 1, state.steps.length - 1);
      const nextStep = state.steps[nextStepIndex] || state.step;

      return {
        step: nextStep,
        currentStepIndex: nextStepIndex
      };
    }),

  onSuccess: (props) =>
    set((state) => ({
      step: props?.skip ? state.step : state.steps[0] || IdentificationSteps.Chip,
      currentStepIndex: props?.skip ? state.currentStepIndex : 0,
      returnToSortingAfterFix: false,
      lastOxManaged: new Date(),
      ox: props?.skip
        ? state.ox
        : {
            sisbov: null,
            chip: null,
            weight: null,
            chipExtra: null,
            dentitionStage: null
          }
    })),

  reset: () =>
    set(() => ({
      data: null,
      batch: null,
      gta: null,
      batchStats: null,
      step: IdentificationSteps.Chip,
      steps: DEFAULT_IDENTIFICATION_STEPS,
      currentStepIndex: 0,
      noAnimalAvailable: false,
      submitSorting: null,
      returnToSortingAfterFix: false,
      ox: {
        sisbov: null,
        chip: null,
        weight: null,
        chipExtra: null,
        dentitionStage: null
      }
    }))
}));
